// layout/Sidebar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/sidebar.css';

export const SIDEBAR_WIDTH = 255;
export const SIDEBAR_COLLAPSED_WIDTH = 60;

export default function Sidebar({ isSidebarOpen, toggleSidebar }) {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEcommerceDropdownOpen, setIsEcommerceDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const ecommerceDropdownRef = useRef(null);
  const [isTempleDropdownOpen, setIsTempleDropdownOpen] = useState(false);
  const templeDropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const navItemsBeforeDropdown = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/UserManagement', label: 'User Management', icon: '👤' },
    { path: '/PurohitManagement', label: 'Purohit Management', icon: '🧘' },    
    { path: '/CategoryManagement', label: 'Category Management', icon: '🔔' },
  ];

  const navItemsAfterDropdown = [
    { path: '/EventManagement', label: 'Event Management', icon: '📅' },
    { path: '/BookingManagement', label: 'Booking Management', icon: '📅' },
    { path: '/PaymentManagement', label: 'Payment Management', icon: '💳' },
    { path: '/NotificationManagement', label: 'Notification', icon: '📈' },
  ];

  const dropdownItems = [
    { path: '/AartiManagement', label: 'Aarti Management' },
    { path: '/MantraList', label: 'Mantra List' },
    { path: '/ArticleManagement', label: 'Article Management' },
    { path: '/YoutubeManagement', label: 'Youtube Management' },
    { path: '/VideoManagement', label: 'Video Management' },
    { path: '/BannerManagement', label: 'Banner Management' },
    { path: '/ContentLibraryManagement', label: 'Content Library Management ' },
  ];

  const ecommerceDropdownItems = [
    { path: '/ProductManagement', label: 'Product Management' },
  ];

  const templeDropdownItems = [
  { path: '/TempleManagement', label: 'Temple Management' },
];

  const width = isSidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (ecommerceDropdownRef.current && !ecommerceDropdownRef.current.contains(event.target)) {
        setIsEcommerceDropdownOpen(false);
      }
      if (templeDropdownRef.current && !templeDropdownRef.current.contains(event.target)) {
        setIsTempleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if any dropdown item is active
  const isDropdownActive = dropdownItems.some(item => isActive(item.path));
  const isEcommerceDropdownActive = ecommerceDropdownItems.some(item => isActive(item.path));
  const isTempleDropdownActive = templeDropdownItems.some(item => isActive(item.path));
  return (
    <aside
      className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: width,
        height: '100vh',
        transition: 'width 0.3s',
      }}
    >
      {/* Fixed Header Section */}
      <div className={`sidebar-top ${!isSidebarOpen ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-toggle">
            <img
              src="/logo.png"
              alt="Logo"
              className={`logo ${isSidebarOpen ? 'large' : 'small'}`}
            />
            <button
              onClick={toggleSidebar}
              className="toggle-button"
              aria-label="Toggle Menu"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Navigation Section */}
      <div className="sidebar-nav">
        <nav role="navigation">
          <ul className="nav-list">
            {/* Items before dropdown */}
            {navItemsBeforeDropdown.map(({ path, label, icon }) => (
              <li key={path} className="nav-item">
                <Link
                  to={path}
                  className={`nav-link ${isActive(path) ? 'active' : ''}`}
                  title={!isSidebarOpen ? label : undefined}
                  aria-current={isActive(path) ? 'page' : undefined}
                  onClick={() => {
                    if (window.innerWidth <= 768) toggleSidebar();
                  }}
                >
                  <span>{icon}</span>
                  {isSidebarOpen && <span>{label}</span>}
                </Link>
              </li>
            ))}

            {/* Content Management Dropdown */}
            <li className="nav-item dropdown-container" ref={dropdownRef}>
              <div
                className={`dropdown-toggle ${isDropdownActive || isDropdownOpen ? 'active' : ''}`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                title={!isSidebarOpen ? 'Content Management' : undefined}
              >
                <span>📂</span>
                {isSidebarOpen && (
                  <span>
                    Content Management {isDropdownOpen ? '▾' : '▸'}
                  </span>
                )}
              </div>

              {isDropdownOpen && isSidebarOpen && (
                <ul className="dropdown-menu inside">
                  {dropdownItems.map(({ path, label }) => (
                    <li key={path}>
                      <Link
                        to={path}
                        className={`dropdown-link ${isActive(path) ? 'active' : ''}`}
                        onClick={() => {
                          setIsDropdownOpen(false);
                          if (window.innerWidth <= 768) toggleSidebar();
                        }}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* E-Commerce Dropdown */}
            <li className="nav-item dropdown-container" ref={ecommerceDropdownRef}>
              <div
                className={`dropdown-toggle ${isEcommerceDropdownActive || isEcommerceDropdownOpen ? 'active' : ''}`}
                onClick={() => setIsEcommerceDropdownOpen(!isEcommerceDropdownOpen)}
                title={!isSidebarOpen ? 'E-Commerce' : undefined}
              >
                <span>🛒</span>
                {isSidebarOpen && (
                  <span>
                    E-Commerce {isEcommerceDropdownOpen ? '▾' : '▸'}
                  </span>
                )}
              </div>

              {isEcommerceDropdownOpen && isSidebarOpen && (
                <ul className="dropdown-menu inside">
                  {ecommerceDropdownItems.map(({ path, label }) => (
                    <li key={path}>
                      <Link
                        to={path}
                        className={`dropdown-link ${isActive(path) ? 'active' : ''}`}
                        onClick={() => {
                          setIsEcommerceDropdownOpen(false);
                          if (window.innerWidth <= 768) toggleSidebar();
                        }}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* Temple Dropdown */}
            <li className="nav-item dropdown-container" ref={templeDropdownRef}>
              <div
                className={`dropdown-toggle ${isTempleDropdownActive || isTempleDropdownOpen ? 'active' : ''}`}
                onClick={() => setIsTempleDropdownOpen(!isTempleDropdownOpen)}
                title={!isSidebarOpen ? 'Temple' : undefined}
              >
                <span>🛕</span>
                {isSidebarOpen && (
                  <span>
                    Temple {isTempleDropdownOpen ? '▾' : '▸'}
                  </span>
                )}
              </div>

              {isTempleDropdownOpen && isSidebarOpen && (
                <ul className="dropdown-menu inside">
                  {templeDropdownItems.map(({ path, label }) => (
                    <li key={path}>
                      <Link
                        to={path}
                        className={`dropdown-link ${isActive(path) ? 'active' : ''}`}
                        onClick={() => {
                          setIsTempleDropdownOpen(false);
                          if (window.innerWidth <= 768) toggleSidebar();
                        }}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* Items after dropdowns */}
            {navItemsAfterDropdown.map(({ path, label, icon }) => (
              <li key={path} className="nav-item">
                <Link
                  to={path}
                  className={`nav-link ${isActive(path) ? 'active' : ''}`}
                  title={!isSidebarOpen ? label : undefined}
                  aria-current={isActive(path) ? 'page' : undefined}
                  onClick={() => {
                    if (window.innerWidth <= 768) toggleSidebar();
                  }}
                >
                  <span>{icon}</span>
                  {isSidebarOpen && <span>{label}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}